/*
 * tss-bundle-pricing.js — TSS Bundle Builder pricing core, 10 Oct 2026
 *
 *   Price = Base(n) + 300 x (A3 books) + Extras(n, choice)
 *
 * Split from tss-bundle-engine.js so the money math is readable and testable
 * on its own, with no DOM in it. The model arrives as JSON from
 * snippets/tss-pricing-data.liquid, so a price change is a Liquid edit.
 *
 * Load this BEFORE tss-bundle-engine.js.
 */
(() => {
  /*
   * Carrier option values are written the way a CUSTOMER should read them,
   * because Shopify builds the variant title out of them and shows it in the
   * cart, the checkout and the order confirmation. Terse values gave a cart
   * line reading "3 / 1 / Complete Kit", which means nothing to a buyer.
   * These strings must match the carrier product's option values exactly.
   */
  const EXTRA_TO_CARRIER = {
    none: 'Books only',
    kit: 'Coloring Kit',
    candle: 'Candle',
    both: 'Complete Kit',
  };

  const booksValue = (tier) => tier + ' Books';
  const a3Value = (count) => (count === 0 ? 'All A4' : count + ' in A3');

  const create = (model) => {
    const api = {
      model: model,
      EXTRA_TO_CARRIER: EXTRA_TO_CARRIER,

      a3Count(books) {
        return books.filter((b) => b.size === 'a3').length;
      },

      extrasUplift(tier, extra) {
        const row = model.extras[String(tier)] || {};
        return Number(row[extra] || 0);
      },

      baseFor(tier) {
        return Number(model.base[String(tier)] || 0);
      },

      /*
       * Per-book figure for the ladder messaging, rounded to the rupee.
       * 1399/2 is 699.50 and 1799/3 is 599.67, but the pricing plan advertises
       * 700 and 600 — so round rather than print a figure nobody wrote down.
       */
      perBook(tier) {
        return Math.round(api.baseFor(tier) / tier / 100) * 100;
      },

      // Advisory until cross-checked against the carrier variant's real price.
      price(state) {
        return (
          api.baseFor(state.tier) +
          api.a3Count(state.books) * Number(model.a3Uplift) +
          api.extrasUplift(state.tier, state.extra)
        );
      },

      // "Bought separately" anchor. Only counts things that genuinely sell on
      // their own, so every "Save X" we print is defensible.
      compare(state) {
        const s = model.standalone;
        let total =
          state.tier * Number(s.book) + api.a3Count(state.books) * Number(model.a3Uplift);
        if (state.extra === 'kit' || state.extra === 'both') total += Number(s.kit);
        if (state.extra === 'candle' || state.extra === 'both') total += Number(s.candle);
        return total;
      },

      label(extra) {
        return model.labels[extra] || extra;
      },

      /*
       * Every price in this store is a whole number of rupees, so this formats
       * to whole rupees. The only fractional values reaching here are per-book
       * averages, which should read 700, not 699.50.
       */
      money(paise) {
        const rupees = Math.round(Number(paise || 0) / 100);
        try {
          return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: model.currency || 'INR',
            maximumFractionDigits: 0,
          }).format(rupees);
        } catch (e) {
          return '₹' + rupees;
        }
      },

      /*
       * Resolve the carrier's option order by NAME rather than trusting a
       * fixed position, so reordering the options in Shopify admin cannot
       * silently map a bundle onto the wrong variant. "A3 Books" contains both
       * "book" and "a3", so the books test excludes anything with "a3".
       */
      carrierOptionIndexes(carrier) {
        const names = (carrier.optionNames || []).map((n) => String(n).toLowerCase());
        const find = (test, fallback) => {
          const i = names.findIndex(test);
          return i > -1 ? i : fallback;
        };
        return {
          books: find((n) => n.indexOf('book') > -1 && n.indexOf('a3') === -1, 0),
          a3: find((n) => n.indexOf('a3') > -1, 1),
          extras: find((n) => n.indexOf('extra') > -1, 2),
        };
      },

      carrierVariant(carrier, state) {
        if (!carrier || !Array.isArray(carrier.variants)) return null;
        const idx = api.carrierOptionIndexes(carrier);
        const wanted = [];
        wanted[idx.books] = booksValue(state.tier);
        wanted[idx.a3] = a3Value(api.a3Count(state.books));
        wanted[idx.extras] = EXTRA_TO_CARRIER[state.extra];

        return (
          carrier.variants.find((v) =>
            wanted.every(
              (w, i) => w === undefined || String((v.options || [])[i]).trim() === String(w)
            )
          ) || null
        );
      },
    };

    return api;
  };

  window.TSSBundlePricing = { create: create, EXTRA_TO_CARRIER: EXTRA_TO_CARRIER };
})();